import {
  DEFAULT_PARAMS,
  EMPTY_PROFILE,
  EMPTY_TEXTS,
  IMPORT_FORMAT,
  IMPORT_VERSION,
  OFFER_FORMAT,
  OFFER_FORMAT_VERSION,
  defaultHoaiInput,
  type ImportDocument,
  type OfferDocumentFile,
  type OfferShare,
} from '@pst/shared';
import { api, exchangeApi, importApi, offersApi, projectApi, projectDataApi, tenderApi } from '../api';

/**
 * The sample project and the sample offer (Beispieldaten): something to
 * click through, made of PST's own import formats and written through the
 * same routes as an import - nothing a module would not accept from a file.
 * Fictitious throughout: no real person, firm, place or cost figure. Dates
 * are relative to today, so the sample always has a past, a present and a
 * future.
 */

export const SAMPLE_PROJECT_NUMBER = 'BSP-01';
export const SAMPLE_OFFER_CODE = 'BSP';

const iso = (d: Date): string => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Day `n` counted from the Monday of the week of `today`. */
function dayFrom(today: Date): (n: number) => string {
  const monday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - ((today.getDay() + 6) % 7));
  return (n) => iso(new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + n));
}

export function sampleProject(today = new Date()): ImportDocument {
  const day = dayFrom(today);
  return {
    format: IMPORT_FORMAT,
    version: IMPORT_VERSION,
    project: {
      number: SAMPLE_PROJECT_NUMBER,
      name: 'Kita Sonnenwinkel, Musterstadt (Beispiel)',
      shortName: 'Kita Sonnenwinkel',
      client: 'Gemeinde Musterstadt (fiktiv)',
      description: 'Beispielprojekt zum Ausprobieren: Neubau einer dreigruppigen Kita in Holzbauweise. Alle Angaben sind erfunden; das Projekt lässt sich löschen und unter Einstellungen › Beispieldaten neu anlegen.',
      offerDate: day(-60),
      offerNet: 148000,
      hardDeadline: day(84),
      planVersion: 'Beispiel',
      facts: [{ label: 'Gruppen', value: '3' }, { label: 'Bauweise', value: 'Holzrahmenbau' }],
      assumptions: [{ marker: 'A', text: 'Baugrundgutachten liegt bis Ende LPH 2 vor' }, { marker: '?', text: 'Förderzusage für Holzbau offen' }],
      risks: [{ code: 'R1', title: 'Förderbescheid verzögert sich', measure: 'Varianten ohne Förderung mitführen' }],
    },
    contacts: [
      { code: 'AG', role: 'Auftraggebende', organisation: 'Gemeinde Musterstadt (fiktiv)', name: 'Erika Beispiel', email: 'erika.beispiel@example.invalid' },
      { code: 'TWP', role: 'Tragwerksplanung', organisation: 'Muster Ingenieure (fiktiv)', name: 'Max Mustermann' },
      { code: 'A', role: 'Architektur', organisation: 'Planungsbüro Beispiel', name: 'Alex Beispiel' },
    ],
    schedule: {
      name: 'Planstand Beispiel',
      bundesland: 'BE',
      targetFinish: day(80),
      baseline: 'Planstand Auftrag',
      groups: [{ code: '1', name: 'Grundlagenermittlung' }, { code: '2', name: 'Vorplanung' }, { code: '3', name: 'Entwurfsplanung' }],
      tasks: [
        { code: '1.1', name: 'Bestandsunterlagen sichten', group: '1', start: day(-14), end: day(-10), owner: 'A', status: 'done', summary: 'Grundlagen' },
        { code: '1.2', name: 'Raumprogramm abstimmen', group: '1', start: day(-9), end: day(-3), owner: 'A/AG', predecessors: ['1.1'], status: 'done', summary: 'Grundlagen' },
        { code: '2.1', name: 'Varianten skizzieren', group: '2', start: day(-2), end: day(9), owner: 'A', predecessors: ['1.2'], status: 'running', summary: 'Vorplanung' },
        { code: '2.2', name: 'Tragwerkskonzept', group: '2', start: day(3), end: day(12), owner: 'TWP', summary: 'Vorplanung' },
        { code: '2.3', name: 'Kostenschätzung', group: '2', start: day(10), end: day(16), owner: 'A', predecessors: ['2.1'], deliverable: 'Kostenschätzung nach Kostengruppen', summary: 'Vorplanung' },
        { code: '3.1', name: 'Entwurf zeichnen', group: '3', start: day(21), end: day(49), owner: 'A', predecessors: ['2.3'], summary: 'Entwurf' },
        { code: '3.2', name: 'Kostenberechnung', group: '3', start: day(50), end: day(60), owner: 'A', predecessors: ['3.1'], deadline: day(62), summary: 'Entwurf' },
      ],
      milestones: [
        { code: 'M1', name: 'Vorplanung abgestimmt', date: day(18), after: ['2.3'] },
        { code: 'M2', name: 'Entwurf freigegeben', date: day(63), after: ['3.2'], criterion: 'Freigabe durch den Gemeinderat (fiktiv)' },
      ],
    },
    costs: {
      vatRate: 19, referenceStage: 'estimate', riskPercent: 10, rangePercent: 15,
      groups: [
        { code: '200', name: 'Vorbereitung (Beispiel)', amounts: { frame: 60000 } },
        { code: '300', name: 'Bauwerk – Konstruktion (Beispiel)', amounts: { frame: 1650000 } },
        { code: '330', name: 'Außenwände (Beispiel)' },
        { code: '360', name: 'Dach (Beispiel)' },
        { code: '400', name: 'Bauwerk – Technik (Beispiel)', amounts: { frame: 520000, estimate: 540000 } },
        { code: '500', name: 'Außenanlagen (Beispiel)', amounts: { frame: 180000, estimate: 175000 } },
        { code: '700', name: 'Nebenkosten (Beispiel)', amounts: { frame: 480000, estimate: 495000 } },
      ],
      values: [
        { name: 'BGF', label: 'Brutto-Grundfläche', kind: 'quantity', formula: '1150', unit: 'm²' },
        { name: 'AWF', label: 'Außenwandfläche', kind: 'quantity', formula: '980', unit: 'm²' },
        { name: 'KW_330', label: 'Außenwand Holzrahmenbau (Annahme)', kind: 'rate', formula: '420', unit: '€/m²' },
      ],
      items: [
        { group: '330', stage: 'estimate', description: 'Außenwände Holzrahmenbau', quantity: 'AWF', unit: 'm²', rate: 'KW_330', source: 'Annahme Büro (Beispiel)', flag: 'A' },
        { group: '360', stage: 'estimate', description: 'Gründach extensiv', quantity: '820', unit: 'm²', rate: '265', flag: '?' },
        { group: '300', stage: 'estimate', description: 'Übrige Konstruktion', quantity: 'BGF', unit: 'm²', rate: '950' },
      ],
    },
    time: {
      categories: [
        { code: 'LPH 1', label: 'Grundlagenermittlung', budgetHours: 60 },
        { code: 'LPH 2', label: 'Vorplanung', budgetHours: 180 },
        { code: 'LPH 3', label: 'Entwurfsplanung', budgetHours: 320 },
      ],
      entries: [
        { date: day(-12), hours: 6, category: 'LPH 1', comment: 'Ortstermin und Bestand' },
        { date: day(-8), hours: 7.5, category: 'LPH 1', taskCode: '1.2' },
        { date: day(-4), hours: 5, category: 'LPH 1', comment: 'Abstimmung Raumprogramm' },
        { date: day(-1), hours: 8, category: 'LPH 2', taskCode: '2.1' },
      ],
    },
  };
}

const noShare = (): OfferShare[] => [{ text: '', hours: null, fixed: null }];

export function sampleOffer(today = new Date()): OfferDocumentFile {
  const day = dayFrom(today);
  const blank = { phone: '', email: '', chamber: '', chamberNo: '', buildingAuthority: '', contact: '', note: '' };
  const pos = (title: string, a: number, z: number | null, share: OfferShare[], owed: string, cuts: boolean[]) => ({
    title, hours: [a, z], shares: share, kind: 'Pauschale', reasoning: 'Beispielansatz', owed, excluded: 'Weitere Varianten, siehe Zusatzleistung Z1.', cuts,
  });
  const data = {
    roles: [{ short: 'A', name: 'Architektin/Architekt', rate: 85, basis: 'Beispielsatz' }, { short: 'Z', name: 'Zeichnerin/Zeichner', rate: 55, basis: 'Beispielsatz' }],
    partyIds: ['P1'],
    subcontractors: [{ partyId: 'P2', name: 'Licht Beispiel GmbH', short: 'L', area: 'Beleuchtung', billing: 'Stunden' as const, purchaseRate: 75, surcharge: 0.05, priceStatus: 'Annahme' as const, address: '', contact: '' }],
    params: { ...DEFAULT_PARAMS },
    positions: [
      pos('Bestandsaufnahme und Grundlagen', 12, 6, noShare(), 'Bestandsplan 1:200, Fotodokumentation', [true, true, true]),
      pos('Freiraumkonzept mit zwei Varianten', 28, 16, [{ text: 'Lichtkonzept', hours: 6, fixed: null }], 'Lageplan 1:200 mit zwei Varianten, Erläuterung', [true, true, false]),
      pos('Abstimmung und Präsentation', 10, 2, noShare(), 'zwei Abstimmungsrunden, eine Präsentation', [true, false, false]),
    ],
    extras: [
      { title: 'Weitere Variante', hours: [8, 4], shares: noShare(), unit: 'je Variante', reasoning: 'Beispielansatz', origin: 'Vorschlag' as const, description: 'Eine weitere Variante im Lageplan.' },
      { title: 'Weitere Abstimmungsrunde', hours: [4, null], shares: noShare(), unit: 'je Runde', reasoning: 'Beispielansatz', origin: 'verlangt' as const, description: 'Vorbereitung, Termin, Protokoll.' },
    ],
    cutNames: ['ohne Präsentation', 'nur Grundlagen', ''],
  };
  return {
    format: OFFER_FORMAT,
    version: OFFER_FORMAT_VERSION,
    exportedAt: today.toISOString(),
    offer: {
      code: SAMPLE_OFFER_CODE,
      name: 'Freiraumkonzept Schulhof (Beispiel)',
      client: 'Stadt Musterstadt (fiktiv)',
      lot: '',
      location: 'Musterstadt',
      state: 'Berlin',
      status: 'entwurf',
      dueDate: day(10),
      offerDate: day(0),
      bindingDays: 60,
      description: 'Beispielangebot zum Ausprobieren. Alle Angaben sind erfunden; es lässt sich löschen und unter Einstellungen › Beispieldaten neu anlegen.',
      texts: {
        ...EMPTY_TEXTS,
        clientContact: 'Erika Beispiel', clientStreet: 'Rathausplatz 1', clientCity: '12345 Musterstadt',
        salutation: 'Damen und Herren',
        subject: 'Freiraumkonzept für den Schulhof (Beispiel)',
        reference: 'Anfrage der Stadt Musterstadt (Beispiel)',
        qualifications: ['Architektin/Architekt, Mitglied der Architektenkammer (Beispiel)'],
        assumptions: ['Bestandspläne werden von der Stadt gestellt.'],
        daysDocuments: '10', daysApprovals: '5', place: 'Musterstadt', start: 'nach Beauftragung', duration: 'vier Wochen', payment: 'nach Leistungsstand, zahlbar binnen 30 Tagen',
        questions: [{ text: 'Gibt es ein Budget für die Umsetzung?', source: 'Anfrage, Abschnitt 2', urgent: true }],
        risks: [{ risk: 'Abstimmung mit der Schulkonferenz dauert länger', cause: 'Termine nur in der Schulzeit', position: '3', likelihood: 3, impact: 2, dimension: 'Termine', consequence: 'Abgabe verschiebt sich', measure: 'Termine früh vereinbaren', owner: 'A', status: 'offen' }],
        attachments: { ...EMPTY_TEXTS.attachments, cvPartyIds: ['P1'], referenceIds: ['R1'] },
      },
      hoai: [{ label: 'Freianlagen Schulhof (Beispiel)', input: { ...defaultHoaiInput('freianlagen'), costs: 180000 }, building: null, points: null }],
      variants: [{ code: 'A', name: 'Mit Lichtplanung', constellation: 'einzel_nu', data }],
    },
    parties: [
      {
        ...blank, key: 'P1', name: 'Alex Beispiel (Beispiel)', profession: 'Architektin/Architekt', street: 'Musterweg 1', city: '12345 Musterstadt', email: 'alex.beispiel@example.invalid', isCompany: false,
        profile: { ...EMPTY_PROFILE, career: [{ period: 'seit 2015', text: 'Planungsbüro Beispiel, Inhaberin/Inhaber' }], education: [{ period: '2008–2014', text: 'Studium der Architektur (Beispiel)' }], languages: 'Deutsch, Englisch' },
      },
      { ...blank, key: 'P2', name: 'Licht Beispiel GmbH', profession: 'Lichtplanung', street: 'Lampenweg 2', city: '12345 Musterstadt', isCompany: true, profile: EMPTY_PROFILE },
    ],
    references: [
      { key: 'R1', party: null, title: 'Pausenhof Grundschule · Beispielstadt', client: 'Gemeinde Beispielstadt (fiktiv)', service: 'Freianlagen, LP 1–5 (Beispiel)', period: '2023', figures: 'Fläche 3.000 m² (Beispiel)', category: 'Freianlagen', note: '', sortOrder: 0 },
    ],
  };
}

export interface SampleResult {
  projectId: string | null;
  offerId: string | null;
  done: string[];
}

/**
 * Creates what is asked for and missing. The project is made like an import:
 * create, then each active module's part; the tendering example is the
 * module's own built-in one, awarded to its cheapest bidder.
 */
export async function createSample(what: { project: boolean; offer: boolean }): Promise<SampleResult> {
  const done: string[] = [];
  let projectId: string | null = null;
  let offerId: string | null = null;
  if (what.project) {
    const doc = sampleProject();
    const active = new Set((await api.modules()).filter((m) => m.active).map((m) => m.key));
    projectId = (await projectApi.create({ number: doc.project.number, name: doc.project.name })).id;
    try {
      await fillSampleProject(projectId, doc, active, done);
    } catch (e) {
      // Half a sample would block making it again: take it back, then report.
      await projectApi.remove(projectId, doc.project.number).catch(() => undefined);
      throw new Error(`Das Beispielprojekt ließ sich nicht vollständig anlegen und wurde wieder entfernt: ${e instanceof Error ? e.message : String(e)}`);
    }
  }
  if (what.offer) {
    const r = await offersApi.importFile(sampleOffer());
    offerId = r.id;
    done.push(`Angebot ${r.code} mit Beteiligten, Referenz und HOAI-Berechnung`);
  }
  return { projectId, offerId, done };
}

async function fillSampleProject(projectId: string, doc: ImportDocument, active: ReadonlySet<string>, done: string[]): Promise<void> {
  done.push(`Projekt ${doc.project.number} angelegt`);
  if (active.has('projectdata')) {
    const { number: _n, name: _m, ...details } = doc.project;
    await projectDataApi.import(projectId, { details, contacts: doc.contacts ?? [] });
    if (doc.time) await exchangeApi.replaceTime(projectId, doc.time);
    done.push('Projektdaten, Kontakte und Zeiterfassung');
  }
  if (active.has('scheduling') && doc.schedule) {
    await importApi.importSchedule(projectId, doc.schedule, false);
    done.push('Terminplan mit Basisplan und vereinfachter Fassung');
  }
  if (active.has('costs') && doc.costs) {
    await exchangeApi.replaceCosts(projectId, doc.costs);
    done.push('Kostenplanung');
  }
  if (active.has('tendering')) {
    const pkg = (await tenderApi.sample(projectId)).id;
    const detail = await tenderApi.get(projectId, pkg);
    if (detail.evaluation.lowestBidderId) {
      await tenderApi.award(projectId, pkg, { bidderId: detail.evaluation.lowestBidderId, awardedOn: iso(new Date()), note: 'Beispiel' });
    }
    done.push('Vergabeeinheit mit Bietern und Zuschlag');
  }
}
